import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getTotalActivityLogs } from "../../../api/getOverviewData";
import SmallOverviewWithIcon from "./SmallOverviewWithIcon";
import { Skeleton } from "@mui/material";
import { getTheme } from "../../../theme";
import EditNoteIcon from "@mui/icons-material/EditNote";

type TotalActivityLogsProps = {
  projectCodes: string[];
  height: number;
};

export type TotalActivityLogsRef = {
  isReady: () => boolean;
  getActivityLogs: () => number[];
};

const TotalActivityLogs = forwardRef<
  TotalActivityLogsRef,
  TotalActivityLogsProps
>(function TotalActivityLogs({ projectCodes, height }, ref) {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<number[]>([]);
  const theme = getTheme();

  useImperativeHandle(ref, () => ({
    isReady: () => isLoading,
    getActivityLogs: () => data,
  }));

  useEffect(() => {
    setIsLoading(true);
    if (projectCodes.length === 0) return;
    let controller = new AbortController();
    let isMounted = true;
    const fetchTotalActivityLogs = async () => {
      try {
        const data = await getTotalActivityLogs(projectCodes, controller);
        if (isMounted) {
          setData(data);
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Fetch error:", error);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchTotalActivityLogs();

    return () => {
      isMounted = false;
      controller.abort(); // Abort the request when effect runs again
    };
  }, [projectCodes]);

  return data.length > 0 && isLoading === false ? (
    <SmallOverviewWithIcon
      height={height}
      titleMain="Total Activity Logs"
      titleSecondary="today"
      contentMain={data[0]}
      contentSecondary={data[1]}
      icon={EditNoteIcon}
      iconColor={theme.palette.terciaryChart}
    />
  ) : (
    <Skeleton
      sx={{
        borderRadius: "5px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
      }}
      animation="pulse"
      variant="rectangular"
      width={"100%"}
      height={height}
    />
  );
});

export { TotalActivityLogs };
